import { curve, heroBackground, romanWarrior } from "../assets";
import Button from "./Button";
import Section from "./Section";
import { BackgroundCircles, BottomLine, Gradient } from "./design/Hero";
import { useRef } from "react";
import CompanyLogos from "./CompanyLogos";

const Hero = () => {
  const parallaxRef = useRef(null);

  return (
    <Section
      className="pt-[12rem] -mt-[5.25rem]"
      crosses
      crossesOffset="lg:translate-y-[5.25rem]"
      customPaddings
      id="hero"
    >
      <div className="container relative" ref={parallaxRef}>
        <div className="relative z-1 max-w-[62rem] mx-auto text-center mb-[3.875rem] md:mb-20 lg:mb-[6.25rem]">
          <h1 className="h1 mb-6">
            AI Vector Graphics, with{" "}
            <span className="inline-block relative">
              Consistent Style{" "}
              <img
                src={curve}
                className="absolute top-full left-0 w-full xl:-mt-2"
                width={624}
                height={28}
                alt="Curve"
              />
            </span>
          </h1>
          <p className="body-1 max-w-3xl mx-auto mb-6 text-n-2 lg:mb-8">
            Generate award-winning Vector illustrations in 30 seconds with AI.
            Download as SVG & PNG.
          </p>
          <Button href="/pricing" white>
            Start Creating
          </Button>
          <p className="mt-2 text-lg font-bold text-white relative">
            Try For Free - No Card Required.
            <span className="absolute inset-0 text-white blur-md opacity-75">
              Try For Free - No Card Required.
            </span>
          </p>
        </div>

        {/* Steps Section */}
        <div className="relative max-w-[100rem] mx-auto px-8 xl:mb-16">
          {" "}
          {/* Reduced bottom margin */}
          <div className="relative z-1 p-3 rounded-2xl bg-conic-gradient">
            <div className="relative bg-n-8 rounded-[1rem] p-8">
              <div className="rounded-b-[0.9rem] overflow-hidden">
                {/* Reduced the overall margin inside the steps section */}
                <div className="relative w-full mx-auto mt-6 mb-16">
                  {" "}
                  {/* Reduced top and bottom margin */}
                  <h2 className="text-3xl font-bold text-center mb-8">
                    {" "}
                    {/* Reduced bottom margin */}
                    How It Works
                  </h2>
                  {/* Step 1 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left mb-12">
                    {" "}
                    {/* Reduced bottom margin */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-bold flex flex-col md:flex-row items-start md:items-center">
                        <span className="inline-block bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-3 py-1 rounded-full flex-shrink-0">
                          Step 1
                        </span>
                        <span className="md:ml-2 mt-2 md:mt-0">
                          Select a prompt template
                        </span>
                      </h3>
                      <p className="text-gray-300">
                        A prompt template is a text description of the elements
                        of a particular art style. Choose from our library of
                        existing templates or create your own.
                      </p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <h4 className="text-white font-bold">
                        Monochromatic silhouette
                      </h4>
                      <p className="text-gray-400">
                        "Vector illustration, monochromatic silhouette, single
                        color with varying stroke weights."
                      </p>
                    </div>
                  </div>
                  {/* Step 2 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left mb-12 md:flex-row-reverse">
                    {" "}
                    {/* Reduced bottom margin */}
                    <div className="bg-gray-800 p-4 rounded-lg">
                      <input
                        className="w-full p-2 rounded-md bg-gray-900 text-white"
                        type="text"
                        placeholder="Roman Warrior with a Sword"
                        disabled={true}
                      />
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-lg font-bold flex flex-col md:flex-row items-start md:items-center">
                        <span className="inline-block bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full flex-shrink-0">
                          Step 2
                        </span>
                        <span className="md:ml-2 mt-2 md:mt-0">
                          Enter a text description
                        </span>
                      </h3>
                      <p className="text-gray-300">
                        Describe in text the details of the graphic that you
                        want to create. Your text descriptions combined with
                        your chosen prompt template represent the prompt input
                        to our AI model.
                      </p>
                    </div>
                  </div>
                  {/* Step 3 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left mb-12">
                    {" "}
                    {/* Reduced bottom margin */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-bold flex flex-col md:flex-row items-start md:items-center">
                        <span className="inline-block bg-gradient-to-r from-green-400 to-blue-500 text-white px-3 py-1 rounded-full flex-shrink-0">
                          Step 3
                        </span>
                        <span className="md:ml-2 mt-2 md:mt-0">
                          Generate your vector illustration
                        </span>
                      </h3>
                      <p className="text-gray-300">
                        Generate your vector illustration based on the input and
                        download the result in SVG or PNG format.
                      </p>
                    </div>
                    <div className="bg-gray-100 p-1 rounded-lg">
                      <img
                        src={romanWarrior}
                        alt="Generated illustration"
                        className="w-full rounded-md"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Gradient />
          </div>
          <div className="absolute -top-[54%] left-1/2 w-[234%] -translate-x-1/2 md:-top-[46%] md:w-[138%] lg:-top-[104%]">
            <img
              src={heroBackground}
              className="w-full"
              width={1440}
              height={1800}
              alt="hero"
            />
          </div>
          <BackgroundCircles />
        </div>

        <CompanyLogos className="hidden relative z-10 mt-20 lg:block" />
      </div>

      <BottomLine />
    </Section>
  );
};

export default Hero;
